'use client';

import { FC } from 'react';
import classNames from 'classnames';

import { Container } from '@/components';
import {
  useSelectedStore,
  useSliceItemsForSelectedStore,
  useSliceVisibility,
} from '@/hooks';
import { CtaFeatureSlice, CtaFeatureSliceDefault } from '@/types';
import { getSlicePadding } from '@/utils';
import type { SliceItemBase } from '@/utils/sliceSsr';

import { DefaultVariation, RenderCta } from './CtaFeature.components';
import styles from './CtaFeature.module.scss';

export type CtaFeatureProps = CtaFeatureSlice & {
  alternateItems?: CtaFeatureSlice['items'];
  initialStoreUrlSlug?: string;
  /** Primary.location_slug visibility from SSR (Video contract). */
  isServerVisible?: boolean;
};

/**
 * Single client entry for all CtaFeature variations.
 * Same SSR props for every variation; behavior differs by variation type:
 * - `default` → filter items (Hero / Banner)
 * - others → show/hide via primary.location_slug (Video)
 */
const CtaFeature: FC<CtaFeatureProps> = ({
  alternateItems,
  initialStoreUrlSlug,
  isServerVisible = true,
  ...slice
}) => {
  const { store } = useSelectedStore();
  const isDefaultCarousel = slice.variation === 'default';

  const itemsForStore = useSliceItemsForSelectedStore<
    CtaFeatureSlice['items'][number] & SliceItemBase
  >({
    alternateItems: alternateItems as (CtaFeatureSlice['items'][number] &
      SliceItemBase)[],
    initialStoreUrlSlug,
    items: (slice.items ?? []) as (CtaFeatureSlice['items'][number] &
      SliceItemBase)[],
    primaryLocationSlug: (slice.primary as { location_slug?: string | null })
      ?.location_slug,
  });

  const isPrimaryVisible = useSliceVisibility({
    isServerVisible,
    locationSlug: (slice.primary as { location_slug?: string | null })
      ?.location_slug,
  });

  if (isDefaultCarousel ? !itemsForStore.length : !isPrimaryVisible) {
    return null;
  }

  const primary = slice?.primary;
  const background = primary?.background_color ?? '';
  const borderTop = primary?.border_section ?? '';
  const borderBottom = primary?.border_section ?? '';
  const paddingSection = primary?.padding_section;
  const fullWidth =
    slice.variation === 'default'
      ? Boolean(
          primary && 'cta_full_width' in primary && primary.cta_full_width,
        )
      : slice.variation === 'imageWithLogoAndButton';

  const imageTwoCTAalignLeft =
    slice.variation === 'imageWithTwoCta' &&
    slice?.primary?.content_align === 'left';

  const storeKey = store?.url_slug ?? initialStoreUrlSlug ?? 'default';
  const resolvedSlice = {
    ...slice,
    items: isDefaultCarousel ? itemsForStore : slice.items ?? [],
  } as CtaFeatureSlice;

  return (
    <section
      className={classNames(
        styles.cta_section,
        {
          [styles.cta_section__spacing_bottom]: primary?.bottom_spacing,
        },
        getSlicePadding(paddingSection),
      )}
      key={storeKey}
      style={{ background, borderTop, borderBottom }}
    >
      <Container
        className={classNames({
          [styles.default_variation_container]: isDefaultCarousel,
          [styles.full_width_container]: fullWidth,
          [styles.centered_button_variation_container]:
            slice.variation === 'centeredButton',
          [styles.image_two_cta_variation_container]:
            slice.variation === 'imageWithTwoCta',
          [styles.image_two_cta_variation_container__align_left]:
            imageTwoCTAalignLeft,
          [styles.title_cta_variation_container]:
            slice.variation === 'titleWithCta',
          [styles.title_two_link_variation_container]:
            slice.variation === 'titleWithTwoLinks',
          [styles.image_title_button_variation_container]:
            slice.variation === 'imageWithTitleAndButton',
          [styles.image_logo_button_variation_container]:
            slice.variation === 'imageWithLogoAndButton',
        })}
      >
        {isDefaultCarousel ? (
          <DefaultVariation
            slice={resolvedSlice as CtaFeatureSliceDefault & CtaFeatureSlice}
          />
        ) : (
          <RenderCta {...resolvedSlice} />
        )}
      </Container>
    </section>
  );
};

export default CtaFeature;
